/**
 * Primary button primitive.
 *
 * Variants map to the design's button kit:
 *   primary → solid coral on cream, white text, shadow lift (CTAs)
 *   ink     → solid ink on cream, bg text (secondary "Continue with Apple")
 *   cream   → cream/surface bg, ink text, no shadow (light secondary)
 *   outline → transparent w/ 1.5px ink border ("Sign up with email")
 *   ghost   → transparent w/ ink text (nav-like tertiary)
 *
 * Sizes: sm 36 / md 46 / lg 56 in px. Padding scales with height. Font
 * weight is always 600 — readability over decoration on coloured pills.
 */
import { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Spinner } from './Spinner';

export type BtnVariant = 'primary' | 'ink' | 'cream' | 'outline' | 'ghost';
export type BtnSize = 'sm' | 'md' | 'lg';

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> & {
  variant?: BtnVariant;
  size?: BtnSize;
  full?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  style?: CSSProperties;
  children?: ReactNode;
};

const HEIGHT: Record<BtnSize, number> = { sm: 36, md: 46, lg: 56 };
const PAD_X: Record<BtnSize, number> = { sm: 14, md: 20, lg: 24 };
const FONT_SIZE: Record<BtnSize, number> = { sm: 13, md: 15, lg: 17 };

export function Btn({
  variant = 'primary',
  size = 'md',
  full,
  loading,
  icon,
  style,
  disabled,
  children,
  ...rest
}: Props) {
  const h = HEIGHT[size];

  const palette: Record<BtnVariant, { bg: string; fg: string; border: string; shadow: string }> = {
    primary: { bg: 'var(--coral)',   fg: '#fff',        border: 'none',                       shadow: '0 8px 20px rgba(232,106,76,0.3)' },
    ink:     { bg: 'var(--ink)',     fg: 'var(--bg)',   border: 'none',                       shadow: 'none' },
    cream:   { bg: 'var(--surface)', fg: 'var(--ink)',  border: '1px solid var(--line)',      shadow: 'none' },
    outline: { bg: 'transparent',    fg: 'var(--ink)',  border: '1.5px solid var(--ink)',     shadow: 'none' },
    ghost:   { bg: 'transparent',    fg: 'var(--ink)',  border: 'none',                       shadow: 'none' },
  };
  const p = palette[variant];

  return (
    <button
      disabled={disabled || loading}
      {...rest}
      style={{
        height: h,
        padding: `0 ${PAD_X[size]}px`,
        background: p.bg,
        color: p.fg,
        border: p.border,
        borderRadius: h,
        fontFamily: 'var(--font-ui)',
        fontWeight: 600,
        fontSize: FONT_SIZE[size],
        letterSpacing: -0.1,
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        opacity: disabled || loading ? 0.4 : 1,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        width: full ? '100%' : undefined,
        boxShadow: p.shadow,
        transition: 'transform 0.15s ease, opacity 0.2s ease',
        ...style,
      }}
      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; rest.onMouseDown?.(e); }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; rest.onMouseUp?.(e); }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; rest.onMouseLeave?.(e); }}
    >
      {loading ? <Spinner size={size === 'lg' ? 18 : 14} /> : icon}
      {children}
    </button>
  );
}
